import { State } from '../store/store';
import { Block, Blocks, BlockList } from 'factories/block';

export const getBlock = (blocks: Blocks, id: string): Block => {
  return blocks[id];
};

export const getBlockFromState = (state: State, id: string): Block =>
  getBlock(state.blocks, id);

export type Selector = (block: Block) => boolean;

export const findBlocks = (blocks: Blocks, selector: Selector): Block[] => {
  const keys = Object.keys(blocks).filter(k => {
    return selector(blocks[k]);
  });

  return keys.map(k => blocks[k]).filter(i => i);
};

// using a BFS search filter blocks down to a single new root block provided
export const findBlocksIn = (
  blocks: Blocks,
  block: Block,
  selector: Selector
): Block[] => {
  let toVisit = [] as string[];
  let discovered = [] as string[];

  // Find all initial block children as "seeds".
  if (block && block.children && Array.isArray(block.children)) {
    block.children.forEach(k => {
      toVisit.push(k.id);
      discovered.push(k.id);
    });
  }

  for (var i = 0; toVisit.length > 0; i++) {
    const b = blocks[toVisit.shift()!];
    if (b && b.children && Array.isArray(b.children)) {
      b.children.forEach(k => {
        toVisit.push(k.id);
        discovered.push(k.id);
      });
    }
  }

  const keys = discovered.filter(k => {
    return selector(blocks[k]);
  });

  return keys.map(k => blocks[k]);
};

const getBlockChildren = (blocks: Blocks, block: Block): Blocks => {
  if (Array.isArray(block.children)) {
    // turn block children from refs into blocks
    const blockList = [] as Block[];

    block.children.forEach(block => {
      if (block.type === 'Ref') {
        blockList.push(blocks[block.id]);
      } else {
        blockList.push(block);
      }
    });

    // turn an array of blocks into object of blocks and include all the blocks
    // children
    return blockList.reduce((acc, cur) => {
      if (!cur) {
        return acc;
      }

      return {
        ...acc,
        ...{ [cur.id]: cur },
        ...getBlockChildren(blocks, cur),
      };
    }, {});
  }
  // no block children, just return an empty object
  return {};
};

export const filterToNewRoot = (blocks: Blocks, rootBlock: Block): Blocks => {
  return getBlockChildren(blocks, rootBlock);
};

export const getAllTopics = (blocks: Blocks): BlockList => {
  const keys = Object.keys(blocks).filter(k => {
    return blocks[k].type === 'Topic';
  });

  return keys.map(k => blocks[k]).filter(i => i);
};

export const getTopicGroup = (blocks: Blocks): Block => {
  let groupBlock: Block;

  const possibleGroups = findBlocks(
    blocks,
    (b => b.meta && b.meta.subType === 'Topic') as Selector
  );

  if (possibleGroups.length === 1) {
    groupBlock = possibleGroups[0];
  } else {
    throw new Error('Multiple AnimationGroups found in list');
  }

  return groupBlock;
};

export const getTopicInteractionGroup = (
  blocks: Blocks,
  block: Block
): Block => {
  if (block?.type !== 'Topic') {
    throw new Error(
      `Must provide a block of type: Topic; type "${block?.type}" provided instead`
    );
  }

  let groupBlock: Block;
  const newRootBlockList = filterToNewRoot(blocks, block);
  const possibleGroups = findBlocks(
    newRootBlockList,
    (b => b.meta && b.meta.subType === 'Interaction') as Selector
  );

  if (possibleGroups.length === 1) {
    groupBlock = possibleGroups[0];
  } else {
    throw new Error('Multiple AnimationGroups found in list');
  }

  return groupBlock;
};

export const getTopicGroupFromState = (state: State) =>
  getTopicGroup(state.blocks);

export const getTopicsFromGroup = (
  state: State,
  topicGroup: Block
): BlockList => {
  if (Array.isArray(topicGroup.children)) {
    return topicGroup.children
      .map(c => {
        return state.blocks[c.id];
      })
      .filter(i => i);
  }
  return [];
};

//
export const getInteractionsFromTopicAndState = (
  state: State,
  topic: Block
): BlockList => {
  const interactionGroup = getTopicInteractionGroup(state.blocks, topic);
  if (interactionGroup && Array.isArray(interactionGroup.children)) {
    let interactions = interactionGroup.children
      .map(c => {
        return state.blocks[c.id];
      })
      .filter(i => i);

    if (topic?.props?.asAssessment) {
      // console.log(topic.props, 'isAssessment');

      const introduction: Block | undefined = interactions.find(
        i => i?.props?.type === 'AssessmentIntroduction'
      );
      const questions: Block[] | undefined = interactions.filter(
        q => q?.props?.type === 'AssessmentQuestion'
      );
      const results: Block | undefined = interactions.find(
        i => i?.props?.type === 'AssessmentResults'
      );

      if (!introduction || !questions || !results) {
        throw new Error(
          'NO ASSESSMENT WILL BE RETURNED HERE. MISSING : introduction || questions || results !!!'
        );
      }

      return [introduction, ...questions, results];
    }

    // return interactionGroup.children
    //   .map(c => {
    //     return state.blocks[c.id];
    //   })
    //   .filter(i => i);

    return interactions;
  }
  return [];
};

//
let allInteractions;

export const getAllInteractions = (state: State): BlockList => {
  if (!window.dev?.() && allInteractions) {
    return allInteractions;
  }

  allInteractions = getAllTopics(state.blocks)
    .map((topic: Block) => getInteractionsFromTopicAndState(state, topic))
    .reduce((acc, cur) => [...acc, ...cur], []);

  return allInteractions;
};

//
let allTopicsOrdered;

export const getAllTopicsOrdered = (blocks: Blocks): BlockList => {
  if (!window.dev?.() && allTopicsOrdered) {
    return allTopicsOrdered;
  }

  let groupBlock: Block;

  const possibleGroups = findBlocks(
    blocks,
    (b =>
      b.meta && b.type == 'AnimationGroup' && b.meta.subType === 'Topic') as any
  );

  if (possibleGroups.length === 1) {
    groupBlock = possibleGroups[0];
  } else {
    throw new Error('Multiple AnimationGroups found in list');
  }

  allTopicsOrdered = (groupBlock.children as Block[])
    .map((k: Block) => blocks[k.id])
    .filter(i => i);

  return allTopicsOrdered;
};

//
let allInteractionsOrdered;

export const getAllInteractionsOrdered = (state: State): BlockList => {
  if (!window.dev?.() && allInteractionsOrdered) {
    return allInteractionsOrdered;
  }

  allInteractionsOrdered = getAllTopicsOrdered(state.blocks)
    .map((topic: Block) => getInteractionsFromTopicAndState(state, topic))
    .reduce((acc, cur) => [...acc, ...cur], []);

  return allInteractionsOrdered;
};

export const isRefBlock = (block: Block): boolean => {
  return block.type === 'Ref';
};
